import type { ConvSettings } from './conversationTypes.js';
import type { ConversationStartContext } from './hooks/useStartContext.js';

/**
 * Arma los `moduleHints` iniciales del handshake WS (Record<string,string> —
 * todo viaja serializado). Función pura: la combinación de start context +
 * settings define cómo abre la sesión el backend.
 */
export function buildStartModuleHints(
  start: ConversationStartContext,
  settings: ConvSettings,
  incognitoAtStart: boolean,
): Record<string, string> {
  return {
    // Modo de SESIÓN del módulo english (backend `SessionMode`): Speak es
    // "Conversación libre" → 'free', salvo el examen de nivelación opt-in
    // → 'calibration' (el distiller estima el nivel). NO confundir con
    // settings.mode (vv/vt/tv/tt = modo de I/O, viaja por initialIoMode).
    mode: start.examMode ? 'calibration' : 'free',
    intensity: settings.intensity,
    // El toggle `phonetic` del user MANEJA el motor de PA, no solo el display:
    // si está off, el backend NO llama a phonos (ver el flip mid-sesión via
    // sendSessionUpdate). `moduleHints` es Record<string,string>; el backend lo
    // parsea con `readBooleanHint` (resume.ts), que reconoce
    // 'true'/'1'/'false'/'0' — el string 'on' caía en null y se descartaba.
    pronunciationAssessment: settings.phonetic ? 'true' : 'false',
    // Búsqueda web nativa: el user puede desactivarla en las opciones. Default
    // on. El backend la combina con el kill-switch del módulo + la capability
    // del modelo. Surte efecto al iniciar la sesión.
    webSearch: settings.webSearch ? 'true' : 'false',
    // Correcciones por voz (manos libres): mandamos el EFECTIVO — la coda solo
    // tiene sentido con el master de correcciones ON. El backend lo combina con
    // su kill-switch (`module_config.languages.voiceCorrectionsEnabled`) y solo
    // habla con salida de voz. También muta mid-sesión vía sendSessionUpdate.
    voiceCorrections: settings.correction && settings.voiceCorrections ? 'true' : 'false',
    // Idioma del STT (ElevenLabs Scribe). Sólo lo mandamos cuando el user eligió
    // explícitamente ('auto'|'en'); si es null el backend deriva POR NIVEL
    // (A0/sin-nivel → autodetect, A1+ → en). El `language_code` se fija en el
    // handshake del WS → aplica al iniciar esta charla.
    ...(settings.sttLanguage ? { sttLanguage: settings.sttLanguage } : {}),
    // `no_verbatim` del STT: limpiar muletillas/disfluencias. Default off
    // (verbatim). El backend lo lee con readBooleanHint.
    cleanTranscript: settings.cleanTranscript ? 'true' : 'false',
    // Presente sólo cuando se viene de "Continuar" en el historial.
    ...(start.resumeSessionId ? { resumeSessionId: start.resumeSessionId } : {}),
    // Escenario: curado → `scenarioId`; generado → `inlineScenario` (JSON que
    // el wsServer parsea).
    ...(start.scenario?.scenarioId ? { scenarioId: start.scenario.scenarioId } : {}),
    ...(start.scenario?.inlineScenario
      ? { inlineScenario: JSON.stringify(start.scenario.inlineScenario) }
      : {}),
    // Charla privada: presente sólo si ya venía activada al arrancar. El
    // caso normal (toggle mid-sesión) viaja por POST /sessions/:id/incognito.
    ...(incognitoAtStart ? { incognito: 'true' } : {}),
    // Programa: fuerza mode='plan' + la unit elegida. Va al final para que
    // el `mode` override-e al 'free'/'calibration' de arriba.
    ...(start.overrideUnitId ? { mode: 'plan', overrideUnitId: start.overrideUnitId } : {}),
    // Sub-unidad puntual elegida (sólo con override de unit) → el backend la
    // fija como activa en vez del resume point.
    ...(start.overrideUnitId && start.overrideSubUnitId
      ? { overrideSubUnitId: start.overrideSubUnitId }
      : {}),
    // Dirección "Escuchar" (19.C): el capítulo escuchado viaja serializado en
    // `moduleHints.listen`. El runtime de languages lo resuelve contra
    // `listen_*` e inyecta el transcript como FOCO.
    ...(start.listenStart ? { listen: JSON.stringify(start.listenStart) } : {}),
    // Dirección "Órbitas" (plan 44 F1b): las cards del feed que el user acaba de
    // ver viajan serializadas en `moduleHints.short` como REFERENCIAS
    // (slug + cefr). El runtime de languages las resuelve contra `shorts` e
    // inyecta la card como FOCO — el texto nunca sale del cliente.
    ...(start.shortStart ? { short: JSON.stringify(start.shortStart) } : {}),
    // Pieza Cabina de «Armá tu nave»: el coach abre pidiendo quién sos y para
    // qué querés el inglés, como promete la pieza (#575). El backend lo ignora
    // con un seed o en `starter`.
    ...(start.getToKnow ? { opener: 'get-to-know' } : {}),
    // Charla guiada A0 (plan 33): fuerza `mode='a0'` + la misión. Va al final
    // para que el `mode` override-e a los de arriba. El backend (wsServer
    // allow-list) acepta `a0MissionId`; el runtime resuelve la misión por ese
    // campo SIN tocar `scenarioId` (aislamiento Fase 0).
    ...(start.a0Mission
      ? { mode: 'a0', a0MissionId: start.a0Mission.missionId }
      : {}),
  };
}
